// Drops <title>, meta description, OG/Twitter, canonical URL, and JSON-LD
// structured data into the document head. No SSR yet; we rely on Google's
// JS execution for indexing. Run-of-page side-effect; cleans up on unmount.
import { useEffect } from 'react';
import { config } from '../lib/config';

interface Props {
  title: string;
  description: string;
  /** absolute or path-only canonical URL — defaults to the current path */
  canonical?: string;
  /** OG image URL; the Cloudflare-hosted public/og.png is the fallback */
  image?: string;
  /** any number of JSON-LD payloads — each becomes its own <script> tag */
  jsonLd?: Array<Record<string, unknown>>;
  /** allows/disallows search-engine indexing (default index, follow) */
  noindex?: boolean;
}

const SITE_URL = `https://${config.app.domain}`;
/** Returns the right per-language OG image URL based on the active i18n
 *  locale, falling back to the English default if the language code is
 *  unknown or the active language can't be detected (SSR / first paint). */
function defaultOgForLang(): string {
  try {
    const lang = document.documentElement.lang || 'en';
    return `${SITE_URL}/og/${lang}.svg`;
  } catch {
    return `${SITE_URL}/branding/og-template.svg`;
  }
}

export function SeoHead({
  title, description, canonical, image, jsonLd, noindex,
}: Props) {
  useEffect(() => {
    const path =
      canonical?.startsWith('http')
        ? canonical
        : `${SITE_URL}${canonical ?? window.location.pathname}`;

    // Mark every node we add so we can remove only ours on unmount
    const owned: HTMLElement[] = [];

    document.title = title;
    upsertMeta('description', description, owned);
    upsertMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow', owned);

    upsertProperty('og:type', 'website', owned);
    upsertProperty('og:title', title, owned);
    upsertProperty('og:description', description, owned);
    upsertProperty('og:url', path, owned);
    upsertProperty('og:image', image ?? defaultOgForLang(), owned);
    upsertProperty('og:site_name', config.app.domain, owned);

    upsertMeta('twitter:card', 'summary_large_image', owned);
    upsertMeta('twitter:title', title, owned);
    upsertMeta('twitter:description', description, owned);
    upsertMeta('twitter:image', image ?? defaultOgForLang(), owned);

    upsertCanonical(path, owned);

    for (const payload of jsonLd ?? []) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.seoOwned = '1';
      script.text = JSON.stringify(payload);
      document.head.appendChild(script);
      owned.push(script);
    }

    return () => {
      for (const node of owned) node.remove();
    };
  }, [title, description, canonical, image, noindex, jsonLd]);

  return null;
}

// ---------------------------------------------------------------------------
// helpers — each remembers which nodes we created so unmount cleans up safely
// ---------------------------------------------------------------------------

function upsertMeta(name: string, content: string, owned: HTMLElement[]) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  let created = false;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
    created = true;
  }
  tag.setAttribute('content', content);
  if (created) {
    tag.dataset.seoOwned = '1';
    owned.push(tag);
  }
}

function upsertProperty(prop: string, content: string, owned: HTMLElement[]) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[property="${prop}"]`);
  let created = false;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', prop);
    document.head.appendChild(tag);
    created = true;
  }
  tag.setAttribute('content', content);
  if (created) {
    tag.dataset.seoOwned = '1';
    owned.push(tag);
  }
}

function upsertCanonical(href: string, owned: HTMLElement[]) {
  let tag = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  let created = false;
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
    created = true;
  }
  tag.setAttribute('href', href);
  if (created) {
    tag.dataset.seoOwned = '1';
    owned.push(tag);
  }
}
